import { useState } from 'react';
import { Plus, Minus, Info } from 'lucide-react';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import Slider from '../ui/Slider';
import SliderValue from '../ui/SliderValue';
import Disclaimer from '../ui/Disclaimer';
import { DISCLAIMER_CONTENT } from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

type AnnualIncomeFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function AnnualIncomeForm({ formData, onSubmit }: AnnualIncomeFormProps) {
  const [income, setIncome] = useState(formData.annualIncome || 0);
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();
  const minIncome = 20000;
  const maxIncome = 250000;
  const step = 5000;

  const handleSliderChange = (value: number) => {
    setIncome(value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSubmit = () => {
    onSubmit({ annualIncome: income });
  };

  const handleIncrement = () => {
    const newValue = Math.min((income || 0) + step, maxIncome);
    handleSliderChange(newValue);
  };

  const handleDecrement = () => {
    const newValue = Math.max((income || 0) - step, minIncome);
    handleSliderChange(newValue);
  };

  return (
    <div id="annual-income-form" className="space-y-8">
      <div className="text-center">
        <Title 
          id="annual-income-title"
          name="annual-income-title"
          as="h1" 
          className="mb-2"
        >
          Annual Income
        </Title>
        <Description 
          id="annual-income-description"
          name="annual-income-description"
          size="lg"
        >
          What is your total annual income?
          <span className="ml-2 inline-flex items-center relative group">
            <Info className="w-5 h-5 text-gray-600 cursor-pointer" />
            <span className="absolute left-0 transform -translate-x-full mt-2 w-64 p-2 bg-gray-700 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
              Total annual income is your “gross income before taxes.”
            </span>
          </span>
        </Description>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <button
            id="annual-income-decrease"
            name="annual-income-decrease"
            type="button"
            onClick={handleDecrement}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label="Decrease income"
          >
            <Minus className="w-5 h-5 text-gray-600" />
          </button>
          
          <SliderValue
            id="annual-income-value"
            name="annual-income-value"
            value={income || 0}
            min={minIncome}
            max={maxIncome}
            formatValue={formatCurrency}
          />
          
          <button
            id="annual-income-increase"
            name="annual-income-increase"
            type="button"
            onClick={handleIncrement}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label="Increase income"
          >
            <Plus className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <Slider
          id="annual-income-slider"
          name="annual-income-slider"
          value={income || 0}
          min={minIncome}
          max={maxIncome}
          step={step}
          onChange={handleSliderChange}
          formatValue={formatCurrency}
          showLimits
        />

        <Button
          id="annual-income-submit"
          name="annual-income-submit"
          onClick={handleSubmit}
          fullWidth
          size="lg"
        >
          Continue
        </Button>

        <Disclaimer
          id="annual-income-disclaimer"
          name="annual-income-disclaimer"
        >
          Provide your total available income including wages, retirement, investments, and rental properties. Alimony, child support or separate maintenance is optional and does not need to be included if you do not wish it to be considered as a basis for repaying the loan. Increase any non taxable income or benefits by 25%.
        </Disclaimer>

        {showDisclaimer && (
          <Disclaimer
            id="annual-income-general-disclaimer"
            name="annual-income-general-disclaimer"
          >
            {DISCLAIMER_CONTENT}
          </Disclaimer>
        )}
      </div>
    </div>
  );
}